import { useState, useEffect, useMemo } from 'react';
import { Calendar, ChevronRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  useJackpotsList,
  useJackpotDetail,
} from '@/api/extensions/useJackpotOps';
import { formatDateRange } from '@/utils/formatters';

const SESSION_KEY = 'delegate-event-selector';

interface EventSelectorProps {
  onEventSelect: (eventId: string) => void;
  selectedEventId?: string | null;
}

export function EventSelector({ onEventSelect, selectedEventId }: EventSelectorProps) {
  const [jackpotId, setJackpotId] = useState<string>(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      return saved ? JSON.parse(saved).jackpotId ?? '' : '';
    } catch {
      return '';
    }
  });

  const { data: jackpotsList, isLoading: jackpotsLoading } = useJackpotsList({
    pageSize: 100,
  });
  const { data: jackpotDetail, isLoading: detailLoading } = useJackpotDetail(jackpotId);

  const jackpots = jackpotsList?.data ?? [];
  const events = useMemo(() => (jackpotDetail as any)?.events ?? [], [jackpotDetail]);

  // Persist selection
  useEffect(() => {
    try {
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ jackpotId, eventId: selectedEventId }),
      );
    } catch {
      // ignore
    }
  }, [jackpotId, selectedEventId]);

  // Restore saved event on mount
  useEffect(() => {
    if (!selectedEventId) {
      try {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (saved) {
          const { eventId: savedEventId } = JSON.parse(saved);
          if (savedEventId) onEventSelect(savedEventId);
        }
      } catch {
        // ignore
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJackpotChange = (id: string) => {
    setJackpotId(id);
  };

  const handleBack = () => {
    setJackpotId('');
  };

  return (
    <div className="space-y-3">
      {/* Jackpot Selector */}
      <div className="flex items-center gap-3">
        {jackpotId && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <Select value={jackpotId} onValueChange={handleJackpotChange}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={jackpotsLoading ? 'Loading...' : 'Select a jackpot'} />
          </SelectTrigger>
          <SelectContent>
            {jackpots.map((j: any) => (
              <SelectItem key={j.id} value={j.id}>
                {j.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Events Grid */}
      {jackpotId && (
        detailLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No events in this jackpot.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {events.map((ev: any) => {
              const isSelected = selectedEventId === ev.id;
              return (
                <Card
                  key={ev.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    isSelected ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => onEventSelect(ev.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-medium text-sm truncate">{ev.event_name}</h4>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {ev.event_type && (
                        <Badge variant="secondary" className="text-xs">{ev.event_type}</Badge>
                      )}
                      {ev.status && (
                        <StatusBadge status={ev.status} variant="event" />
                      )}
                    </div>
                    {(ev.start_date || ev.end_date) && (
                      <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDateRange(ev.start_date, ev.end_date)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
